import { useEffect, useRef, useState } from 'react';
import { Badge } from './ui';

/**
 * Anbindung einer seriellen Waage (z. B. ADE EHR1, RS-232) über die Web Serial
 * API - ohne Treiber oder lokalen Agenten. Der Packplatz-PC braucht Chrome oder
 * Edge und HTTPS; der Nutzer gibt den COM-Port einmal frei, der Browser merkt
 * es sich für die Herkunft.
 *
 * Das Ausgabeformat serieller Waagen ist herstellerabhängig, aber fast immer
 * ASCII mit einer Gewichtszahl je Zeile (oft mit "kg" und einem Stabil-Kennzeichen
 * wie "ST"/"US"). Deshalb wird tolerant geparst: die erste Dezimalzahl der Zeile
 * ist das Gewicht; "US" (unstable) markiert einen noch schwankenden Wert. Die
 * Rohzeile wird angezeigt, damit sich das Format zur Not am echten Gerät ablesen
 * und der Parser nachziehen lässt.
 */

const BAUD_KEY = 'wawi.waage.baud';
const BAUDS = [1200, 2400, 4800, 9600, 19200];

export function Waage({ onGewicht }: { onGewicht: (kg: number) => void }) {
  const unterstuetzt = typeof navigator !== 'undefined' && 'serial' in navigator;
  const [verbunden, setVerbunden] = useState(false);
  const [baud, setBaud] = useState<number>(() => Number(localStorage.getItem(BAUD_KEY)) || 9600);
  const [gewicht, setGewicht] = useState<number | null>(null);
  const [stabil, setStabil] = useState(false);
  const [roh, setRoh] = useState('');
  const [fehler, setFehler] = useState('');

  const portRef = useRef<any>(null);
  const readerRef = useRef<any>(null);
  const laufen = useRef(false);

  function verarbeite(zeile: string) {
    setRoh(zeile);
    const m = zeile.match(/(-?\d+[.,]\d+)/);
    if (!m) return;
    const wert = parseFloat(m[1]!.replace(',', '.'));
    if (!Number.isFinite(wert)) return;
    // "US" = unstable; sonst als stabil werten.
    setStabil(!/\bUS\b/i.test(zeile));
    setGewicht(wert);
  }

  async function lesen() {
    const port = portRef.current;
    if (!port?.readable) return;
    const stream = port.readable.pipeThrough(new (window as any).TextDecoderStream());
    const reader = stream.getReader();
    readerRef.current = reader;
    let puffer = '';
    try {
      while (laufen.current) {
        const { value, done } = await reader.read();
        if (done) break;
        puffer += value;
        let idx: number;
        while ((idx = puffer.search(/[\r\n]/)) >= 0) {
          const zeile = puffer.slice(0, idx).trim();
          puffer = puffer.slice(idx + 1);
          if (zeile) verarbeite(zeile);
        }
      }
    } catch {
      /* Lesen abgebrochen oder Port getrennt */
    } finally {
      try {
        reader.releaseLock();
      } catch {
        /* egal */
      }
    }
  }

  async function verbinden() {
    setFehler('');
    try {
      const port = await (navigator as any).serial.requestPort();
      await port.open({ baudRate: baud });
      localStorage.setItem(BAUD_KEY, String(baud));
      portRef.current = port;
      laufen.current = true;
      setVerbunden(true);
      void lesen();
    } catch (e: any) {
      setFehler(e?.message || 'Verbindung fehlgeschlagen');
    }
  }

  async function trennen() {
    laufen.current = false;
    try {
      await readerRef.current?.cancel();
    } catch {
      /* egal */
    }
    try {
      await portRef.current?.close();
    } catch {
      /* egal */
    }
    portRef.current = null;
    setVerbunden(false);
    setGewicht(null);
    setRoh('');
  }

  useEffect(() => {
    return () => {
      laufen.current = false;
      try {
        readerRef.current?.cancel();
      } catch {
        /* egal */
      }
      try {
        portRef.current?.close();
      } catch {
        /* egal */
      }
    };
  }, []);

  if (!unterstuetzt) {
    return (
      <label className="field" style={{ gridColumn: '1 / -1' }}>
        <span>Waage</span>
        <span className="small muted">
          Dieser Browser unterstützt keine serielle Verbindung. Für die Waage bitte Chrome oder Edge
          am Packplatz verwenden.
        </span>
      </label>
    );
  }

  return (
    <label className="field" style={{ gridColumn: '1 / -1' }}>
      <span>Waage (ADE EHR1)</span>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {!verbunden ? (
          <>
            <select value={baud} onChange={(e) => setBaud(Number(e.target.value))} style={{ width: 'auto' }}>
              {BAUDS.map((b) => (
                <option key={b} value={b}>
                  {b} Baud
                </option>
              ))}
            </select>
            <button type="button" onClick={() => void verbinden()}>
              Waage verbinden
            </button>
          </>
        ) : (
          <>
            <strong style={{ fontSize: 22, minWidth: 110 }}>
              {gewicht != null ? `${gewicht.toFixed(3)} kg` : '—'}
            </strong>
            <Badge kind={stabil ? 'ok' : 'warn'}>{stabil ? 'stabil' : 'schwankt'}</Badge>
            <button
              type="button"
              className="primary"
              disabled={gewicht == null}
              onClick={() => gewicht != null && onGewicht(gewicht)}
            >
              Gewicht übernehmen
            </button>
            <button type="button" onClick={() => void trennen()}>
              trennen
            </button>
          </>
        )}
      </div>
      {roh ? <span className="small muted">Rohdaten: {roh}</span> : null}
      {fehler ? <span className="small" style={{ color: '#b3261e' }}>{fehler}</span> : null}
    </label>
  );
}
